import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
    Button,
    Grid,
    Typography,
    Paper,
    Box,
    Chip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import ShowToast from 'components/ShowToast';
import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL } from 'assets/constants/Constants';
import { getEnquiryById, createPurchaseOrder, getS3PresignedUrl, getPoByEnquiryId, updatePurchaseOrder } from 'lib/axiosUtils'; 
import { HTTP_HEADER_KEYS, HTTP_CONTENT_TYPES } from 'assets/constants/apiConstants';
import { CloudUpload as CloudUploadIcon, DeleteForever as DeleteForeverIcon } from '@mui/icons-material';
import '../BOM/bom.css';

const CreatePoPage = () => {
    const { id: enquiryId } = useParams();
    const history = useHistory();
    const [enquiry, setEnquiry] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [enquiryViewOpen, setEnquiryViewOpen] = useState(false);
    
    // PO Form State
    const [poData, setPoData] = useState({
        po_number: '',
        po_date: '',
        po_value: '',
        payment_terms: '',
        delivery_terms: ''
    });

    const [existingPoId, setExistingPoId] = useState(null);
    const [existingAttachments, setExistingAttachments] = useState([]);

    const [poAttachmentsDraft, setPoAttachmentsDraft] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Enquiry
                const enqRes = await getEnquiryById({}, {}, enquiryId);
                if (enqRes.status === 'SUCCESS' || enqRes.statusCode === 200) {
                    setEnquiry(enqRes.data.result);
                }

                // Fetch existing PO if any
                const poRes = await getPoByEnquiryId({}, {}, enquiryId);
                if (poRes.status === 'SUCCESS' || poRes.statusCode === 200) {
                    const po = poRes.data.result;
                    setExistingPoId(po._id);
                    setPoData({
                        po_number: po.po_number || '',
                        po_date: po.po_date ? new Date(po.po_date).toISOString().substring(0, 10) : '',
                        po_value: po.po_value || '',
                        payment_terms: po.payment_terms || '',
                        delivery_terms: po.delivery_terms || ''
                    });
                    setExistingAttachments(po.po_attachments || []);
                }
            } catch (err) {
                // If PO fetch fails, it might just not exist yet.
                // We show an error only if the Enquiry fetch failed, but since we combined them:
                console.log("Enquiry or PO fetch error:", err);
            }
        };
        if (enquiryId) fetchData();
    }, [enquiryId]);

    const handleFileSelect = (e) => {
        if (!e.target.files) return;
        const files = Array.from(e.target.files);
        setPoAttachmentsDraft(prev => [...prev, ...files]);
        e.target.value = '';
    };

    const removeDraftFile = (index) => {
        setPoAttachmentsDraft(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingFile = (index) => {
        setExistingAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const uploadFilesToS3 = async (files) => {
        if (!files.length) return [];
        const uploaded = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const presignedRes = await getS3PresignedUrl({}, {
                fileName: file.name,
                fileType: file.type || 'application/octet-stream'
            });
            const putUrl = presignedRes?.data?.url;
            const publicUrl = presignedRes?.data?.fileUrl;
            if (putUrl) {
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('PUT', putUrl, true);
                    xhr.setRequestHeader(HTTP_HEADER_KEYS.CONTENT_TYPE, file.type || HTTP_CONTENT_TYPES.OCTET_STREAM);
                    xhr.upload.onprogress = (e) => {
                        if (e.lengthComputable) {
                            const pct = Math.round(((i + (e.loaded / e.total)) / files.length) * 100);
                            setUploadPct(pct);
                        }
                    };
                    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject());
                    xhr.onerror = () => reject();
                    xhr.send(file);
                });

                let file_key = '';
                try {
                    const u = new URL(putUrl);
                    const path = decodeURIComponent(u.pathname.replace(/^\/+/, ''));
                    const at = path.indexOf('uploads/');
                    file_key = at >= 0 ? path.substring(at) : path;
                } catch (e) {}

                uploaded.push({
                    file_name: file.name,
                    file_type: file.type,
                    file_url: publicUrl,
                    file_key: file_key
                });
            }
        }
        return uploaded;
    };

    const handleSavePO = async () => {
        if (!poData.po_number || !poData.po_date) {
            ShowToast('PO Number and Date are required', TYPE_FAIL, INTERVAL);
            return;
        }

        setIsSaving(true);
        try {
            let final_attachments = [...existingAttachments];
            if (poAttachmentsDraft.length > 0) {
                setIsUploading(true);
                try {
                    const uploaded_attachments = await uploadFilesToS3(poAttachmentsDraft);
                    final_attachments = [...final_attachments, ...uploaded_attachments];
                } catch (uploadErr) {
                    ShowToast('File upload failed', TYPE_FAIL, INTERVAL);
                    setIsUploading(false);
                    setIsSaving(false);
                    return;
                }
                setIsUploading(false);
                setUploadPct(0);
            }

            const payload = { ...poData, enquiry_id: enquiryId, po_attachments: final_attachments };
            let res;
            if (existingPoId) {
                res = await updatePurchaseOrder(existingPoId, payload);
            } else {
                res = await createPurchaseOrder(payload);
            }
            
            if (res.status === 'SUCCESS' || res.statusCode === 201 || res.statusCode === 200) {
                ShowToast(existingPoId ? 'Purchase Order Updated Successfully!' : 'Purchase Order Created Successfully!', TYPE_SUCCESS, INTERVAL);
                history.push(existingPoId ? '/po-list' : '/enquiry-list'); 
            } else {
                ShowToast(res.data?.message || 'Failed to save PO', TYPE_FAIL, INTERVAL);
            }
        } catch (error) {
            ShowToast(error.response?.data?.message || 'Failed to create PO', TYPE_FAIL, INTERVAL);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="main-content bom-page">
            <div className="bom-table-header">
                <div className="bom-table-title">{existingPoId ? 'Update Purchase Order' : 'Convert Enquiry to Purchase Order'}</div>
            </div>

            <Paper sx={{ p: 3, m: 2 }}>
                {/* Context Section */}
                {enquiry && (
                    <Box mb={4} p={2} sx={{ background: '#f5f7f8', borderLeft: '4px solid #2d3d42', borderRadius: '4px' }}>
                        <Typography variant="" sx={{ color: '#2d3d42', mb: 1 }}>
                            <strong>Enquiry Reference:</strong>{' '}
                            <Link
                                to="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setEnquiryViewOpen(true);
                                }}
                                style={{ color: '#1976d2', textDecoration: 'none' }}
                                title="View Enquiry"
                                >
                                {enquiry.enquiryNumber || '-'}
                            </Link>
                        </Typography>
                        <Typography variant="subtitle2">
                            <strong>Customer:</strong> {enquiry.customer?.customer_name}
                        </Typography>
                        <Typography variant="subtitle2">
                            <strong>Machine:</strong> {enquiry.machineManufacturer} {enquiry.machineModel}
                        </Typography>
                    </Box>
                )}


                {/* Upload progress bar */}
                {isUploading && (
                    <Box sx={{ mb: 2, px: 1 }}>
                        <LinearProgress variant="determinate" value={uploadPct} sx={{ height: 6, borderRadius: 3 }} />
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>{uploadPct}% Uploading...</Typography>
                    </Box>
                )}

                {/* PO Input Form */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <label className="form-label d-block bom-label" style={{ marginBottom: '6px' }}>
                            PO Number <span className="text-danger ms-1">*</span>
                        </label>
                        <input 
                            type="text"
                            className="form-control-input bom-input"
                            style={{ width: '100%', padding: '6px 12px', border: '1px solid #ccc', borderRadius: '4px' }}
                            value={poData.po_number}
                            onChange={(e) => setPoData({...poData, po_number: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <label className="form-label d-block bom-label" style={{ marginBottom: '6px' }}>
                            PO Date <span className="text-danger ms-1">*</span>
                        </label>
                        <input 
                            type="date"
                            className="form-control-input bom-input"
                            style={{ width: '100%', padding: '6px 12px', border: '1px solid #ccc', borderRadius: '4px' }}
                            value={poData.po_date}
                            onChange={(e) => setPoData({...poData, po_date: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <label className="form-label d-block bom-label" style={{ marginBottom: '6px' }}>
                            PO Value (Optional)
                        </label>
                        <input 
                            type="number"
                            className="form-control-input bom-input no-spin"
                            style={{ width: '100%', padding: '6px 12px', border: '1px solid #ccc', borderRadius: '4px' }}
                            value={poData.po_value}
                            onChange={(e) => setPoData({...poData, po_value: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <label className="form-label d-block bom-label" style={{ marginBottom: '6px' }}>
                            Payment Terms (Optional)
                        </label>
                        <input 
                            type="text"
                            className="form-control-input bom-input"
                            style={{ width: '100%', padding: '6px 12px', border: '1px solid #ccc', borderRadius: '4px' }}
                            value={poData.payment_terms}
                            onChange={(e) => setPoData({...poData, payment_terms: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <label className="form-label d-block bom-label" style={{ marginBottom: '6px' }}>
                            Delivery Terms (Optional)
                        </label>
                        <textarea 
                            className="form-control-input bom-input"
                            style={{ width: '100%', padding: '6px 12px', border: '1px solid #ccc', borderRadius: '4px', minHeight: '60px' }}
                            rows={2}
                            value={poData.delivery_terms}
                            onChange={(e) => setPoData({...poData, delivery_terms: e.target.value})}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <label className="form-label d-block bom-label">PO Attachments</label>
                        <div style={{ border: '1px dashed #ccc', borderRadius: '8px', padding: '12px', background: '#fafbfc', minHeight: '120px' }}>
                            <Button component="label" startIcon={<CloudUploadIcon />} size="small" sx={{ textTransform: 'none', color: '#2d3d42' }}>
                                Upload PO Document
                                <input type="file" hidden multiple onChange={handleFileSelect} />
                            </Button>
                            {(existingAttachments.length > 0 || poAttachmentsDraft.length > 0) && (
                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {existingAttachments.map((f, i) => (
                                        <Chip key={`exist-po-${i}`} label={f.file_name || 'Document'} size="small" color="secondary" variant="outlined"
                                            onDelete={() => removeExistingFile(i)} deleteIcon={<DeleteForeverIcon />} />
                                    ))}
                                    {poAttachmentsDraft.map((f, i) => (
                                        <Chip key={`draft-po-${i}`} label={f.name} size="small" color="primary" variant="outlined"
                                            onDelete={() => removeDraftFile(i)} deleteIcon={<DeleteForeverIcon />} />
                                    ))}
                                </Box>
                            )}
                        </div>
                    </Grid>
                </Grid>

                <div className="row mt-4 mb-3">
                    <div className="col-md-12 d-flex justify-content-end">
                        <button
                            type="button"
                            className="brand-button-inverse m-3"
                            onClick={() => history.goBack()}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="release-to-manufacturing-button m-3"
                            onClick={handleSavePO}
                            disabled={isSaving || isUploading}
                        >
                            {isUploading ? 'Uploading files...' : isSaving ? 'Saving...' : existingPoId ? 'Update Purchase Order' : 'Save Purchase Order'}
                        </button>
                    </div>
                </div>
            </Paper>
            <Dialog
                open={enquiryViewOpen}
                onClose={() => setEnquiryViewOpen(false)}
                maxWidth={false}
                fullWidth={false}
                scroll="paper"
                sx={{
                    '& .MuiDialog-container': {
                        alignItems: 'flex-start !important',
                        justifyContent: 'center',
                    },
                    '& .MuiPaper-root': {
                        marginTop: '140px !important',
                    },
                }}
                PaperProps={{
                    sx: {
                         width: '950px',
                        maxWidth: 'calc(100vw - 32px)',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
                    },
                }}
            >
                <DialogContent sx={{ p: 0 }}>
                    <div
                        style={{
                            padding: '14px 18px',
                            borderBottom: '1px solid #ddd',
                            position: 'relative',
                            fontSize: '18px',
                            fontWeight: 400,
                            color: '#2d3d42',
                        }}
                    >
                        Enquiry Details

                        <button
                            type="button"
                            onClick={() => setEnquiryViewOpen(false)}
                            style={{
                                position: 'absolute',
                                right: '14px',
                                top: '10px',
                                border: 'none',
                                background: 'transparent',
                                fontSize: '25px',
                                lineHeight: 1,
                                color: '#888',
                                cursor: 'pointer',
                            }}
                        >
                            &times;
                        </button>
                    </div>

                    <div style={{ padding: '14px 32px 36px' }}>
                        {enquiry && (
                            <div
                                style={{
                                    background: '#f8f9fa',
                                    padding: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #dee2e6',
                                    minHeight: '170px',
                            }}
                        >
                            <p style={{ marginBottom: '16px' }}>
                                <b>Enquiry Number:</b> {enquiry.enquiryNumber || '-'}
                            </p>

                            <p style={{ marginBottom: '16px' }}>
                                <b>Customer:</b> {enquiry.customer?.customer_name || '-'}
                            </p>

                            <p style={{ marginBottom: '16px' }}>
                                <b>Region:</b> {enquiry.region?.name || '-'}
                            </p>

                            <p style={{ marginBottom: '16px' }}>
                                <b>Status:</b> {enquiry.status || '-'}
                            </p>

                            <p style={{ marginBottom: 0 }}>
                                <b>Created Date:</b>{' '}
                                {enquiry.created_date_time
                                    ? moment(enquiry.created_date_time).format('DD MMM YYYY')
                                    : '-'}
                            </p>
                        </div>
                     )}
                    </div>
                </DialogContent>
            </Dialog>

        </div>
    );
};

export default CreatePoPage;
